import { create } from "zustand";
import {
  ENGINE_VERSION, ScenarioFileSchema, ScenarioSchema, applyMethodRules, buildScenario, customEquipment, instantiateTemplate, moveEquipment, newId, rechainH2, renumber,
  type ActivityInput, type CalcResult, type Equipment, type Example, type ExclusionReason, type Location, type MethodId, type Scenario, type ScenarioFile, type Stage,
} from "@h2lca/core";
import { api } from "@/lib/api";
import { useMaster, methodById, templateById } from "./master";

/* ── 산정 단계 ──────────────────────────────────────────────── */
export const STEPS = [
  { id: "basis", label: "산정목적과 기본조건", short: "기본조건" },
  { id: "fu", label: "기능단위와 기준흐름", short: "기능단위" },
  { id: "equipment", label: "설비 구성", short: "설비" },
  { id: "boundary", label: "시스템 경계 설정", short: "경계" },
  { id: "inputs", label: "운전자료 입력", short: "운전자료" },
  { id: "calc", label: "계산과정 확인", short: "계산과정" },
  { id: "result", label: "결과 확인", short: "결과" },
] as const;
export type StepId = (typeof STEPS)[number]["id"];
export const STEP_IDS = STEPS.map((s) => s.id) as StepId[];

const draftKey = (ownerId: string) => `h2lca.draft.v2:${ownerId}`;

export interface SavedSummary {
  engineVersion?: string;
  perFunctionalUnitKgCO2eq?: number | null;
  functionalUnitKg?: number;
  status: string;
  totalKgCO2eq: number | null;
  perKgH2: number | null;
}

export function resultSummary(result: CalcResult): SavedSummary {
  return { engineVersion: ENGINE_VERSION, status: result.status, totalKgCO2eq: result.totalKgCO2eq, perKgH2: result.perKgH2, perFunctionalUnitKgCO2eq: result.perFunctionalUnitKgCO2eq, functionalUnitKg: result.functionalUnitKg };
}

interface WorkshopState {
  draftOwnerId: string | null;
  setDraftOwner: (ownerId: string | null) => void;
  scenario: Scenario | null;
  /** 서버 보관함의 id , 새로 만든 시나리오는 null */
  serverId: string | null;
  dirty: boolean;
  step: StepId;
  selectedEquipmentId: string | null;
  saving: boolean;
  lastSavedAt: string | null;

  setStep: (step: StepId) => void;
  next: () => void;
  prev: () => void;
  select: (id: string | null) => void;

  newScenario: (productionTechId: string, name?: string) => void;
  newFromPathway: (opts: { techId: string; routeId: string; electricityPresetId: string; method: MethodId; name?: string }) => void;
  loadExample: (example: Example) => void;
  loadScenario: (scenario: Scenario, serverId?: string | null, savedAt?: string | null) => void;
  restoreDraft: () => boolean;
  reset: () => void;

  /** 시나리오를 통째로 바꾸는 일반 갱신 , 나머지 액션은 전부 이걸 거친다 */
  update: (fn: (s: Scenario) => Scenario) => void;
  addFromTemplate: (templateId: string) => string | null;
  addCustom: (stage: Stage, location: Location, name?: string) => string;
  removeEquipment: (id: string) => void;
  move: (id: string, dir: -1 | 1) => void;
  setBoundary: (id: string, inBoundary: boolean, reason?: ExclusionReason, note?: string) => void;
  updateEquipment: (id: string, patch: Partial<Omit<Equipment, "id" | "inputs" | "h2">>) => void;
  updateInput: (equipmentId: string, inputId: string, patch: Partial<ActivityInput>) => void;
  addInput: (equipmentId: string, input: Omit<ActivityInput, "id">) => void;
  removeInput: (equipmentId: string, inputId: string) => void;
  setH2: (equipmentId: string, patch: Partial<Equipment["h2"]>) => void;
  rechain: () => void;

  saveToServer: (summary?: SavedSummary) => Promise<string>;
  loadFromServer: (id: string) => Promise<void>;
  exportFile: () => void;
  importFile: (file: File) => Promise<void>;
}

function touch(s: Scenario): Scenario {
  return { ...s, meta: { ...s.meta, updatedAt: new Date().toISOString() } };
}

function persistDraft(ownerId: string | null, scenario: Scenario | null, serverId: string | null, step: StepId, lastSavedAt: string | null = null, dirty = true) {
  if (!ownerId) return;
  try {
    if (!scenario) localStorage.removeItem(draftKey(ownerId));
    else localStorage.setItem(draftKey(ownerId), JSON.stringify({ scenario, serverId, step, lastSavedAt, dirty }));
  } catch {
    /* 저장 공간이 없거나 차단된 브라우저 , 조용히 넘어간다 */
  }
}

export const useWorkshop = create<WorkshopState>((set, get) => {
  const apply = (fn: (s: Scenario) => Scenario, extra: Partial<WorkshopState> = {}) => {
    const cur = get().scenario;
    if (!cur) return;
    const next = touch(fn(cur));
    set({ scenario: next, dirty: true, ...extra });
    persistDraft(get().draftOwnerId, next, get().serverId, get().step, get().lastSavedAt);
  };

  return {
    draftOwnerId: null,
    setDraftOwner: (ownerId) => {
      if (get().draftOwnerId === ownerId) return;
      // 계정이 바뀌면 메모리도 비운다. 각 계정의 임시 작업은 다시 로그인할 때 복원한다.
      set({ draftOwnerId: ownerId, scenario: null, serverId: null, dirty: false, step: "basis", selectedEquipmentId: null, saving: false, lastSavedAt: null });
      if (ownerId) get().restoreDraft();
    },
    scenario: null,
    serverId: null,
    dirty: false,
    step: "basis",
    selectedEquipmentId: null,
    saving: false,
    lastSavedAt: null,

    setStep: (step) => {
      set({ step });
      persistDraft(get().draftOwnerId, get().scenario, get().serverId, step, get().lastSavedAt, get().dirty);
    },
    next: () => {
      const i = STEP_IDS.indexOf(get().step);
      const n = STEP_IDS[Math.min(i + 1, STEP_IDS.length - 1)]!;
      get().setStep(n);
    },
    prev: () => {
      const i = STEP_IDS.indexOf(get().step);
      const p = STEP_IDS[Math.max(i - 1, 0)]!;
      get().setStep(p);
    },
    select: (id) => set({ selectedEquipmentId: id }),

    newScenario: (productionTechId, name) => {
      const { data } = useMaster.getState();
      const tech = data.technologies.find((t) => t.id === productionTechId);
      const s: Scenario = {
        schemaVersion: 1,
        name: name ?? `새 시나리오: ${tech?.name ?? productionTechId}`,
        meta: { purpose: "", baseYear: new Date().getFullYear(), createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
        method: "general",
        basis: { productionTechId, batchH2Kg: 100 },
        functionalUnit: { h2Kg: 1, purityPct: 99.97, pressureBar: 200, state: "gas", deliveryPoint: "생산시설 출하지점" },
        electricity: { mode: "share", sources: [{ sourceId: data.sources[0]?.id ?? "grid_kr", share: 1 }] },
        allocation: { policy: "none", byproducts: [] },
        equipment: [],
      };
      set({ scenario: s, serverId: null, dirty: true, step: "basis", selectedEquipmentId: null });
      persistDraft(get().draftOwnerId, s, null, "basis");
    },
    newFromPathway: (opts) => {
      const { data } = useMaster.getState();
      const tech = data.technologies.find((t) => t.id === opts.techId);
      const method = methodById(data, opts.method);
      const built = buildScenario(
        {
          techId: opts.techId,
          routeId: opts.routeId,
          electricityPresetId: opts.electricityPresetId,
          method: opts.method,
          name: opts.name,
        },
        data.templates,
        tech?.name,
      );
      const s = applyMethodRules(built, method);
      set({ scenario: s, serverId: null, dirty: true, step: "basis", selectedEquipmentId: null, lastSavedAt: null });
      persistDraft(get().draftOwnerId, s, null, "basis");
    },
    loadExample: (example) => {
      const s = structuredClone(example.scenario);
      s.meta.createdAt = new Date().toISOString();
      s.meta.updatedAt = s.meta.createdAt;
      set({ scenario: s, serverId: null, dirty: true, step: "basis", selectedEquipmentId: null });
      persistDraft(get().draftOwnerId, s, null, "basis");
    },
    loadScenario: (scenario, serverId = null, savedAt = null) => {
      set({ scenario, serverId, dirty: false, step: "basis", selectedEquipmentId: null, lastSavedAt: savedAt });
      persistDraft(get().draftOwnerId, scenario, serverId, "basis", savedAt, false);
    },
    restoreDraft: () => {
      try {
        const ownerId = get().draftOwnerId;
        if (!ownerId) return false;
        // v1 임시 작업에는 소유자 정보가 없어 다른 계정으로 자동 이전하지 않는다.
        const raw = localStorage.getItem(draftKey(ownerId));
        if (!raw) return false;
        const parsed = JSON.parse(raw) as { scenario: unknown; serverId: string | null; step: StepId | "method"; lastSavedAt?: string | null; dirty?: boolean };
        const r = ScenarioSchema.safeParse(parsed.scenario);
        if (!r.success) return false;
        // 이전 산정 방식 단계에서 멈춘 작업은 다음 입력 단계로 이어 간다.
        const step = parsed.step === "method" ? "fu" : STEP_IDS.includes(parsed.step) ? parsed.step : "basis";
        const serverId = parsed.serverId ?? null;
        const lastSavedAt = parsed.lastSavedAt ?? null;
        const dirty = parsed.dirty !== false;
        set({ scenario: r.data, serverId, dirty, step, lastSavedAt });
        if (step !== parsed.step) persistDraft(get().draftOwnerId, r.data, serverId, step, lastSavedAt, dirty);
        return true;
      } catch {
        return false;
      }
    },
    reset: () => {
      set({ scenario: null, serverId: null, dirty: false, step: "basis", selectedEquipmentId: null });
      persistDraft(get().draftOwnerId, null, null, "basis");
    },

    update: (fn) => apply(fn),
    addFromTemplate: (templateId) => {
      const s = get().scenario;
      if (!s) return null;
      const t = templateById(useMaster.getState().data, templateId);
      if (!t) return null;
      // 수소가 지나는 설비는 지금 흐름의 끝 값을, 나머지는 산정 기준량을 기준으로 기본값을 채운다
      const chain = rechainH2(s.equipment);
      const last = [...chain].reverse().find((e) => e.h2.role !== "none" && e.inBoundary);
      const flowKg = last?.h2.outKg ?? s.basis.batchH2Kg;
      const eq = instantiateTemplate(t, s.equipment.length, t.h2.role === "passthrough" ? flowKg : s.basis.batchH2Kg);
      // 인증제 모드면 규칙을 바로 적용한다
      const m = methodById(useMaster.getState().data, s.method);
      apply((cur) => applyMethodRules({ ...cur, equipment: renumber([...cur.equipment, eq]) }, m), { selectedEquipmentId: eq.id });
      return eq.id;
    },
    addCustom: (stage, location, name) => {
      const s = get().scenario;
      const eq = customEquipment(s?.equipment.length ?? 0, stage, location, name);
      apply((cur) => ({ ...cur, equipment: renumber([...cur.equipment, eq]) }), { selectedEquipmentId: eq.id });
      return eq.id;
    },
    removeEquipment: (id) => apply((s) => ({ ...s, equipment: renumber(s.equipment.filter((e) => e.id !== id)) }), { selectedEquipmentId: get().selectedEquipmentId === id ? null : get().selectedEquipmentId }),
    move: (id, dir) => apply((s) => ({ ...s, equipment: moveEquipment(s.equipment, id, dir) })),
    setBoundary: (id, inBoundary, reason, note) =>
      apply((s) => ({
        ...s,
        equipment: s.equipment.map((e) => (e.id === id ? { ...e, inBoundary, exclusionReason: inBoundary ? undefined : (reason ?? e.exclusionReason ?? "outside_boundary"), exclusionNote: inBoundary ? undefined : note ?? e.exclusionNote } : e)),
      })),
    updateEquipment: (id, patch) => apply((s) => ({ ...s, equipment: s.equipment.map((e) => (e.id === id ? { ...e, ...patch } : e)) })),
    updateInput: (equipmentId, inputId, patch) =>
      apply((s) => ({
        ...s,
        equipment: s.equipment.map((e) =>
          e.id !== equipmentId
            ? e
            : {
                ...e,
                inputs: e.inputs.map((i) => {
                  if (i.id !== inputId) return i;
                  const next = { ...i, ...patch };
                  // 값을 손으로 고치면 '사용자 입력' 으로, 비우면 '자료 미입력' 으로
                  if ("amount" in patch && patch.valueType === undefined) next.valueType = patch.amount === null ? "missing" : "user";
                  return next;
                }),
              },
        ),
      })),
    addInput: (equipmentId, input) => apply((s) => ({ ...s, equipment: s.equipment.map((e) => (e.id === equipmentId ? { ...e, inputs: [...e.inputs, { ...input, id: newId("in") }] } : e)) })),
    removeInput: (equipmentId, inputId) => apply((s) => ({ ...s, equipment: s.equipment.map((e) => (e.id === equipmentId ? { ...e, inputs: e.inputs.filter((i) => i.id !== inputId) } : e)) })),
    setH2: (equipmentId, patch) => apply((s) => ({ ...s, equipment: s.equipment.map((e) => (e.id === equipmentId ? { ...e, h2: { ...e.h2, ...patch } } : e)) })),
    rechain: () => apply((s) => ({ ...s, equipment: rechainH2(s.equipment) })),

    saveToServer: async (summary) => {
      const { scenario, serverId, draftOwnerId } = get();
      if (!scenario) throw new Error("저장할 시나리오가 없습니다");
      set({ saving: true });
      try {
        const body = { scenario, summary };
        const r = serverId
          ? await api<{ id: string; updatedAt: string }>(`/scenarios/${serverId}`, { method: "PUT", json: body })
          : await api<{ id: string; updatedAt: string }>("/scenarios", { method: "POST", json: body });
        if (get().draftOwnerId !== draftOwnerId) throw new Error("계정이 변경되어 저장 상태를 갱신하지 않았습니다.");
        set({ serverId: r.id, dirty: false, lastSavedAt: r.updatedAt, saving: false });
        persistDraft(get().draftOwnerId, scenario, r.id, get().step, r.updatedAt, false);
        return r.id;
      } catch (e) {
        if (get().draftOwnerId === draftOwnerId) set({ saving: false });
        throw e;
      }
    },
    loadFromServer: async (id) => {
      const ownerId = get().draftOwnerId;
      const r = await api<{ id: string; scenario: unknown; updatedAt: string }>(`/scenarios/${id}`);
      if (get().draftOwnerId !== ownerId) return;
      const parsed = ScenarioSchema.parse(r.scenario);
      get().loadScenario(parsed, r.id, r.updatedAt);
    },
    exportFile: () => {
      const s = get().scenario;
      if (!s) return;
      const file: ScenarioFile = { app: "h2lca-edu", fileVersion: 1, exportedAt: new Date().toISOString(), scenario: s };
      const blob = new Blob([JSON.stringify(file, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${s.name.replace(/[\\/:*?"<>|]+/g, "_")}.h2lca.json`;
      a.click();
      URL.revokeObjectURL(url);
    },
    importFile: async (file) => {
      const text = await file.text();
      const json = JSON.parse(text) as unknown;
      const r = ScenarioFileSchema.safeParse(json);
      if (r.success) return get().loadScenario(r.data.scenario, null);
      // 봉투 없이 시나리오만 든 파일도 받아 준다
      const s = ScenarioSchema.safeParse(json);
      if (!s.success) throw new Error("이 프로그램의 시나리오 파일이 아닙니다");
      get().loadScenario(s.data, null);
    },
  };
});

/** 단계 번호(1부터) */
export function stepIndex(step: StepId): number {
  return STEP_IDS.indexOf(step) + 1;
}
